import { Axis } from "cesium";
import { Matrix3 } from "cesium";
import { Cartesian3 } from "cesium";

// getDirectionalUnitVector
const scratchRotationMatrix = new Matrix3();
const scratchRotatedMatrix = new Matrix3();
const scratchXUnit = new Cartesian3();
const scratchYUnit = new Cartesian3();
const scratchZUnit = new Cartesian3();

export class VectorUtils {
  /**
   * Function that finds the closest point between two vectors starting at point A and B
   * where...
   * a is the vector from starting point A
   * b is the vector from starting point B
   * c is the vector between points A and B
   * @returns The scalar value that, when multiplied by A, will result in the point closest to B
   */
  static getRaysClosestPoint(
    a: Cartesian3,
    b: Cartesian3,
    c: Cartesian3,
  ): number {
    const aa = Cartesian3.dot(a, a);
    const ab = Cartesian3.dot(a, b);
    const ac = Cartesian3.dot(a, c);
    const bb = Cartesian3.dot(b, b);
    const bc = Cartesian3.dot(b, c);
    const d = (-ab * bc + ac * bb) / (aa * bb - ab * ab);
    return d;
  }

  /**
   * Function that returns the unit vector of a vector in a given direction
   * @param bearing_rad The bearing of the vector in radians
   * @param directionMatrix The direction matrix of the vector
   * @param axis The axis to get the unit vector of
   * @returns The unit vector of the vector in the given direction
   */
  static getDirectionalUnitVector(
    bearing_rad: number,
    directionMatrix: Matrix3,
    axis: Axis,
  ): Cartesian3 {
    const rotationMatrix = Matrix3.inverse(
      Matrix3.fromRotationZ(bearing_rad, scratchRotationMatrix),
      scratchRotationMatrix,
    );
    Matrix3.multiply(directionMatrix, rotationMatrix, scratchRotatedMatrix);

    Cartesian3.fromElements(
      scratchRotatedMatrix[0],
      scratchRotatedMatrix[1],
      scratchRotatedMatrix[2],
      scratchXUnit,
    );
    Cartesian3.fromElements(
      scratchRotatedMatrix[3],
      scratchRotatedMatrix[4],
      scratchRotatedMatrix[5],
      scratchYUnit,
    );
    Cartesian3.fromElements(
      scratchRotatedMatrix[6],
      scratchRotatedMatrix[7],
      scratchRotatedMatrix[8],
      scratchZUnit,
    );

    if (axis === Axis.X) {
      return Cartesian3.clone(scratchXUnit);
    } else if (axis === Axis.Y) {
      return Cartesian3.clone(scratchYUnit);
    } else if (axis === Axis.Z) {
      return Cartesian3.clone(scratchZUnit);
    } else {
      console.warn(`Invalid axis: ${axis}`);
      return Cartesian3.clone(scratchZUnit);
    }
  }

  /**
   * Orients a vector to face a given direction by flipping it if it points away
   * @param direction The direction the vector should face towards
   * @param vectorToOrient The vector to orient
   * @returns The same instance of the provided vector, but oriented towards the direction
   */
  static orientTowardsDirection(
    direction: Cartesian3,
    vectorToOrient: Cartesian3,
  ): Cartesian3 {
    if (Cartesian3.dot(vectorToOrient, direction) < 0) {
      Cartesian3.negate(vectorToOrient, vectorToOrient);
    }
    return vectorToOrient;
  }
}
